import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import clsx from 'clsx';
import { isEmpty, cloneDeep, orderBy } from 'lodash';
import { kendoGridInlineStyling } from '../../../helpers/kendoGridInlineStyleHelper';
import { Grid, GridColumn as Column, getSelectedState, GridToolbar } from '@progress/kendo-react-grid';
import { process as kendoProcess, filterBy } from '@progress/kendo-data-query';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { getter } from '@progress/kendo-react-common';
import BobWorkbookModeButton from '../../../components/BobWorkbookModeButton';
import BooleanCellFilter from '../../../components/CustomFilters/BooleanCellFilter';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as classes from './RulesPanel.module.css';

const idGetter = getter('ruleId');

const initialDataStateFilter = {
    logic:   'and',
    filters: []
};

const initialDataState = {
    filter: initialDataStateFilter,
    sort:   [],
    skip:   0
};

const booleanCellFilterValues = [{ text: 'In Fault', operator: 'eq', value: true }, { text: 'No Fault', operator: 'eq', value: false }];
const defaultItem = { text: '(All)', operator: '' };

const FaultCellFilter = (props, selectedBooleanFilter, setSelectedBooleanFilter) => {
    return (
        <BooleanCellFilter
            {...props}
            data={booleanCellFilterValues}
            defaultItem={defaultItem}
            filterValue={selectedBooleanFilter}
            setFilterValue={setSelectedBooleanFilter}
        />
    );
};


const RulesPanel = ({
    bobLoading,
    entityRules,
    entityRulesMeta,
    minFaultHours,
    selectedEquipment,
    selectedSystems,
    selectedRules,
    selectedState,
    selectedStateItems,
    setMinFaultHours,
    setSelectedState,
    setSelectedStateItems,
    setSelectedRules,
    setWorkBookMode,
    workbookModeEnabled,
    rulesTabDataState,
    setRulesTabDataState,
    ...props
}) => {
    // const [dataState, setDataState] = useState(initialDataState);
    const [selectedBooleanFilter, setSelectedBooleanFilter] = useState(defaultItem);

    useEffect(() => {
        if (!workbookModeEnabled) {
            initialDataState.filter = initialDataStateFilter;
            setRulesTabDataState(initialDataState);
        }
    }, [workbookModeEnabled]);

    const onHeaderSelectionChange = event => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(selectedState);
        let selectedStateItems = {};

        const filteredData = orderBy(filterBy(entityRules, rulesTabDataState.filter), rulesTabDataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
            if (checked) {
                selectedStateItems[idGetter(item)] = item;
            }

        });
        if (!checked) {
            selectedStateItems = {};
        }

        setSelectedState(newSelectedState);
        setSelectedStateItems(selectedStateItems);
    };

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey:   'ruleId',
        });

        setSelectedState(newSelectedState);

        if (event && event.dataItem) {
            let data = selectedStateItems;
            let id = event.dataItem.ruleId;

            if (data[id]) {
                delete data[id];
            } else {
                data[id] = event.dataItem;
            }

            setSelectedStateItems(data);
        }
    };

    const onDataStateChange = e => {
        setRulesTabDataState(e.dataState);
    };
    
    const handleFilterHoursChange = e => {
        setMinFaultHours(e.value);
    };

    const hasFaultsCell = props => {
        const {dataItem} = props;
        if (dataItem.hasFaults) {
            props.style.color = 'red';
            return (
                <td 
                    colSpan={props.colSpan} 
                    className={props.className} 
                    role="gridcell" 
                    aria-colindex={props.ariaColumnIndex} 
                    style={props.style}
                >
                    <span className="material-icons-outlined">error</span>
                </td>
            );
        }
        else {
            props.style.color = 'green';
            return (
                <td 
                    className={props.className} 
                    style={props.style}
                    role="gridcell"
                    aria-colindex={props.ariaColumnIndex}
                >
                    <span className="material-icons-outlined">
                        check_circle
                    </span>
                </td>
            );
        }
        
    };

    const handleWorkbookMode = e => {
        setWorkBookMode(!workbookModeEnabled);
    };

    const BuildFaultCellFilterCell = props => {
        return FaultCellFilter(props, selectedBooleanFilter, setSelectedBooleanFilter);
    };

    return (
        <>
            <style>
                { kendoGridInlineStyling }
            </style>
            <div>
                {
                    (isEmpty(selectedSystems) && isEmpty(selectedEquipment)) || isEmpty(entityRulesMeta) ?
                        <div className={classes.NoSelectedRule}>
                            <span className={clsx('material-icons-outlined md-18', classes.Icon)}>
                                info
                            </span>
                            <span className={classes.Text}>Select 'Equipment' and/or 'System' to view rules.</span>
                        </div>
                        :
                        <Grid
                            style={{
                                height: '40rem'
                            }}
                            className={classes.KendoGrid}
                            pageable={false}
                            sortable
                            filterable={workbookModeEnabled}
                            data={kendoProcess(entityRules ? orderBy(entityRules, [rule => rule.ruleName.toLowerCase()], ['asc']).map(item => ({
                                ...item,
                                'selected': selectedState[idGetter(item)],
                            })) : [], rulesTabDataState)}
                            {...rulesTabDataState}
                            reorderable
                            dataItemKey={'ruleId'}
                            selectedField={'selected'}
                            selectable={{
                                enabled: true,
                                drag:    false,
                                cell:    false,
                                mode:    'multiple',
                            }}
                            onSelectionChange={onSelectionChange}
                            onHeaderSelectionChange={onHeaderSelectionChange}
                            onDataStateChange={onDataStateChange}
                        >
                            <GridToolbar>
                                <div className={classes.wrapper}>
                                    <div className={classes.filterCell}>
                                        <p className={classes.filterCellParagraph}>Filter by hours in fault (&gt;)</p>
                                        <NumericTextBox 
                                            defaultValue={0} 
                                            size={'medium'} 
                                            fillMode={'solid'} 
                                            min={0}
                                            width={75}
                                            value={minFaultHours}
                                            onChange={handleFilterHoursChange}
                                        />
                                    </div>
                                    <div className={classes.workbookCell}>
                                        <BobWorkbookModeButton
                                            handleWorkbookMode={handleWorkbookMode}
                                            workbookMode={workbookModeEnabled}
                                        /> 
                                    </div>
                                </div>
                            </GridToolbar>
                            <Column
                                field="selected"
                                width={70}
                                headerSelectionValue={
                                    !isEmpty(entityRules) && entityRules.findIndex((item) => !selectedState[idGetter(item)]) === -1
                                }
                                filterable={false}
                                sortable={false}
                            />
                            <Column
                                field="ruleName"
                                title="Available Rules"
                                id="clientColumn"
                                width={350}
                            />
                            <Column
                                field="hasFaults"
                                title="Has Faults"
                                width={175}
                                filter="boolean"
                                cell={hasFaultsCell}
                                filterCell={BuildFaultCellFilterCell}
                            />
                            <Column
                                field="ruleDescription"
                                title="Description"
                                width="auto"
                            />
                        </Grid>
                }
            </div>
        </>
    );
};

const mapStateToProps = state => ({
    bobLoading:          state.bob.bobLoading,
    entityRules:         state.reporting.entityRules,
    entityRulesMeta:     state.reporting.entityRulesMeta,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...DashboardActions
});

export default connect(mapStateToProps, mapDispatchToProps)(RulesPanel);
